import { useCallback, useEffect, useRef, useState } from "react"
import { LegalContent, TERMS_DOCUMENT } from "./documents"

interface Agreement {
  id: string
  document: string
  version: number
  content: LegalContent
}

interface CheckResult {
  isUserEligible: boolean
  isUserAgreedToTerms: boolean
  requiresAgreement: boolean
  agreement?: Agreement | null
}

interface SaveInput {
  document: string
  version: number
  agreedToTerms: boolean
}

interface Services {
  checkAgreement: (input: { document: string }) => Promise<CheckResult | null>
  saveAgreement: (input: SaveInput) => Promise<{
    agreedToTerms: boolean
    agreementId: string
  } | null>
  // Ordered fallback chain: try each document until one requires acceptance.
  // Ready uses [MASTERY_PREP_TERMS, AOP]; the default is a single document.
  documents?: string[]
  // Optional fail-open: continue past an unloadable chain instead of stranding
  // the user. Ready (AOP-blocking) leaves this false; no app sets it today.
  failOpen?: boolean
}

type Status = "checking" | "required" | "accepted" | "error"

// Stable default so the effect below is not re-run by a fresh array each render.
const DEFAULT_DOCUMENTS = [TERMS_DOCUMENT]

// The app adapter mounts this hook once per authenticated identity. No browser
// cache can stand in for the server's acceptance of the current document version.
export const useRequiredAgreement = ({
  checkAgreement,
  saveAgreement,
  documents = DEFAULT_DOCUMENTS,
  failOpen = false
}: Services) => {
  const [status, setStatus] = useState<Status>("checking")
  const [agreement, setAgreement] = useState<Agreement | null>(null)
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const mounted = useRef(false)
  const saveInFlight = useRef(false)
  // Depend on the document list by contents, not array identity, so a caller
  // passing an inline array does not re-run the check on every render.
  const documentsKey = documents.join("|")

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  useEffect(() => {
    let active = true
    setStatus("checking")
    setError("")
    setAgreement(null)

    const check = async () => {
      // Rebuild the chain from the stable key so the effect depends only on
      // documentsKey (not the array identity). "document" is not shadowed.
      const documentChain = documentsKey.split("|")
      try {
        for (const documentName of documentChain) {
          const result = await checkAgreement({ document: documentName })
          if (!active) return
          if (!result) throw new Error("Agreement check failed")
          // Ineligible users (non-AOP MP Ready) are not subject to these Terms.
          if (!result.isUserEligible) {
            setStatus("accepted")
            return
          }
          if (result.isUserAgreedToTerms && !result.requiresAgreement) {
            setStatus("accepted")
            return
          }
          // A document simply not published in this environment (clean "not
          // required, no record") falls through to the next in the chain — e.g.
          // AOP before the MASTERY_PREP_TERMS migration runs. A failed or
          // malformed check must NOT downgrade to an older document.
          if (!result.requiresAgreement && !result.agreement) continue
          const current = result.agreement
          if (!result.requiresAgreement || !current?.content?.text?.trim() ||
            current.document !== documentName || !current.id ||
            !Number.isInteger(current.version) || current.version < 1 ||
            !["markdown", "text"].includes(current.content.format)) {
            throw new Error("Current terms are unavailable")
          }
          setAgreement(current)
          setStatus("required")
          return
        }
        throw new Error("No agreement document is published")
      } catch (error) {
        if (!active) return
        // failOpen lets the user continue; otherwise eligible AOP users are
        // blocked until they can load and accept a document.
        if (failOpen) {
          console.warn("Terms gate: no agreement document could be loaded; failing open.", error)
          setStatus("accepted")
          return
        }
        console.warn("Terms gate: no agreement document could be loaded; blocking the user.", error)
        setError("We could not load the current Terms of Use. Please retry or log out and try again later.")
        setStatus("error")
      }
    }

    void check()
    return () => { active = false }
  }, [checkAgreement, documentsKey, failOpen, attempt])

  const agree = useCallback(async () => {
    if (!agreement || status !== "required" || saveInFlight.current) return false
    saveInFlight.current = true
    setSaving(true)
    setError("")
    try {
      const result = await saveAgreement({
        document: agreement.document,
        version: agreement.version,
        agreedToTerms: true
      })
      if (!mounted.current) return false
      if (!result?.agreedToTerms || result.agreementId !== agreement.id) {
        throw new Error("Acceptance was not saved")
      }
      setStatus("accepted")
      return true
    } catch {
      if (mounted.current) {
        setError("We could not save your acceptance. Select Continue to retry, or log out and try again later.")
      }
      return false
    } finally {
      saveInFlight.current = false
      if (mounted.current) setSaving(false)
    }
  }, [agreement, status, saveAgreement])

  return { status, agreement, error, saving, agree, retry: () => setAttempt(value => value + 1) }
}
